import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, inject } from "@angular/core";
import { PopoverComponent } from "@tylertech/forge";
import { IGridViewMetadata } from "@tylertech/tyler-api-types";
import { Utilities } from "@tylertech/shared-module";
import { Action } from "@tylertech/commands-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { GridMetadataService } from "../../../services";

export interface ContextMenuAction
{
    type: "feature-action" | "view" | "edit" | "delete" | "copy" | "action";
    row: any;
    action?: any;
}

export interface ContextMenuItem
{
    type: string;
    caption: string;
    handler: Action;
}

@Component({
    selector: "grid-context",
    templateUrl: "./gridContext.tmpl.html",
    standalone: false
})
export class GridContextComponent implements OnInit, OnDestroy
{
    private $gridMetadataService = inject(GridMetadataService);
    private $commonDialogsService = inject(CommonDialogsService);

    @Input() row: any;
    @Input() viewMetadata: IGridViewMetadata;
    @Input() position: { x: number, y: number };
    @Input() isViewOnly: boolean;
    @Input() disableViewAction: boolean;
    @Input() useRepository: boolean;

    @Output() actionSelected = new EventEmitter<ContextMenuAction>();
    @Output() closed = new EventEmitter<void>();

    @ViewChild("popover", { static: true }) popover: PopoverComponent;
    @ViewChild("optionList", { static: true }) optionList: ElementRef<HTMLElement>;

    menuItems: ContextMenuItem[] = [];
    currentFocusIndex = 0;

    ngOnInit(): void
    {
        this.buildMenuItems();
    }

    ngOnDestroy(): void
    {
        this.closed.emit();
    }

    open(): void
    {
        if (this.menuItems.length === 0)
        {
            this.closed.emit();
            return;
        }

        this.popover.open = true;

        requestAnimationFrame(() =>
        {
            const anchor = document.createElement('div');
            anchor.style.position = 'fixed';
            anchor.style.left = `${this.position.x}px`;
            anchor.style.top = `${this.position.y}px`;
            anchor.style.width = '1px';
            anchor.style.height = '1px';
            document.body.appendChild(anchor);

            this.popover.noAnchor = false;
            this.popover.anchorElement = anchor;
            this.popover.placement = 'bottom-start';

            this.focusItem(0);
        });
    }

    close(): void
    {
        this.popover.open = false;
        this.closed.emit();
    }

    private buildMenuItems(): void
    {
        const items: ContextMenuItem[] = [];
        const row = this.row;
        const id = row.Id;

        if (this.hasFeatureAction(row))
        {
            items.push({
                type: "feature-action",
                caption: row.FeatureAction.Caption,
                handler: () => this.onFeatureAction(row)
            });
        }

        if (this.isViewOnly && !this.disableViewAction)
        {
            items.push({
                type: "view",
                caption: "View",
                handler: () => this.onEdit(row, id)
            });
        }

        if (this.$gridMetadataService.canDo("Edit", row["$$model"], this.isViewOnly, this.viewMetadata))
        {
            items.push({
                type: "edit",
                caption: "Edit",
                handler: () => this.onEdit(row, id)
            });
        }

        if (this.$gridMetadataService.canDo("Delete", row["$$model"], this.isViewOnly, this.viewMetadata) && this.useRepository)
        {
            items.push({
                type: "delete",
                caption: "Delete",
                handler: () => this.onDelete(row, id)
            });
        }

        const primaryActions = row.Commands.filter(x => x.Primary && !x.Hidden);
        primaryActions.forEach(action => {
            items.push({
                type: "action",
                caption: action.Caption,
                handler: () => this.onAction(row, action)
            });
        });

        const overflowActions = row.Commands.filter(x => !x.Primary && !x.Hidden);
        overflowActions.forEach(action => {
            items.push({
                type: action.Caption === "Copy" ? "copy" : "action",
                caption: action.Caption,
                handler: () => {
                    if (action.Caption === "Copy") {
                        this.onCopy(row);
                    } else {
                        this.onAction(row, action);
                    }
                }
            });
        });

        this.menuItems = items;
    }

    onItemClick(item: ContextMenuItem, event: Event): void
    {
        event.stopPropagation();
        item.handler();
        this.close();
    }

    onKeyDown(event: KeyboardEvent): void
    {
        const key = event.key;

        if (key === "Tab")
        {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.close();
            return;
        }

        if (key === "Escape")
        {
            event.preventDefault();
            this.close();
            return;
        }

        if (key === "ArrowUp")
        {
            event.preventDefault();
            this.currentFocusIndex--;
            if (this.currentFocusIndex < 0) {
                this.currentFocusIndex = this.menuItems.length - 1;
            }
            this.focusItem(this.currentFocusIndex);
        }

        if (key === "ArrowDown")
        {
            event.preventDefault();
            this.currentFocusIndex++;
            if (this.currentFocusIndex >= this.menuItems.length) {
                this.currentFocusIndex = 0;
            }
            this.focusItem(this.currentFocusIndex);
        }
    }

    onPopoverToggle(): void
    {
        if (!this.popover.open)
            this.close();
    }

    private focusItem(index: number): void
    {
        const buttons = this.optionList.nativeElement.querySelectorAll("forge-list-item button");
        if (buttons[index])
            (buttons[index] as HTMLElement).focus();
    }

    // Action handlers
    private onFeatureAction(row: any): void
    {
        this.actionSelected.emit({
            type: "feature-action",
            row: row
        });
    }

    private onEdit(row: any, id: string): void
    {
        this.actionSelected.emit({
            type: "edit",
            row: row
        });
    }

    private onDelete(row: any, id: string): void
    {
        const msg = [
            "Are you sure you want to delete ",
            row["$$model"]
                ? row["$$model"]["MetadataFormTitle"]
                    ? row["$$model"]["MetadataFormTitle"]
                    : "this item"
                : "this item",
            "?",
        ].join("");

        this.$commonDialogsService.showConfirmationMessage(msg, "Delete Item")
            .then(() => {
                this.actionSelected.emit({
                    type: "delete",
                    row: row
                });
                this.close();
            })
            .catch(() => this.close());
    }

    private onCopy(row: any): void
    {
        this.actionSelected.emit({
            type: "copy",
            row: row
        });
    }

    private onAction(row: any, action: any): void
    {
        this.actionSelected.emit({
            type: "action",
            row: row,
            action: action
        });
    }

    private hasFeatureAction(row: any): boolean
    {
        return !Utilities.IsNullOrUndefined(row?.FeatureAction) && !row.FeatureAction.Hidden;
    }
}
